import { useEffect } from 'react';
import { OnboardingProTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useProOnboardingActionShallowStore } from './use-pro-onboarding-action-store';

type UseProOnboardingPageIntentArgs<TMultiView extends string> = {
  enabled?: boolean;
  activeTab: CallIntelTypes.CallIntelTab;
  summaryView: TMultiView;
  setActiveTab: (tab: CallIntelTypes.CallIntelTab) => void;
  setActiveMultiView: (view: TMultiView) => void;
};

const OVERVIEW_SUMMARY_INTENT_TASKS = [
  OnboardingProTask.PRO_TASK_OVERVIEW_INSIGHTS,
  OnboardingProTask.PRO_TASK_OVERVIEW_CHART_DRILLDOWN,
];

const CALL_RECORDS_INTENT_TASKS = [
  OnboardingProTask.PRO_TASK_VIEW_CALL_INSIGHTS,
  OnboardingProTask.PRO_TASK_REVIEW_CONVERSATION_ANALYSIS,
  OnboardingProTask.PRO_TASK_VIEW_PATIENT_CONTEXT,
];

const FOLLOW_UP_TASKS_INTENT_TASKS = [OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS];

/**
 * Keeps PRO onboarding checklist navigation on the correct Call Intelligence surface.
 */
export function useProOnboardingPageIntent<TMultiView extends string>({
  enabled = true,
  activeTab,
  summaryView,
  setActiveTab,
  setActiveMultiView,
}: UseProOnboardingPageIntentArgs<TMultiView>) {
  const { pendingTask } = useProOnboardingActionShallowStore('pendingTask');

  useEffect(() => {
    if (!enabled) return;
    if (!pendingTask) return;

    if (OVERVIEW_SUMMARY_INTENT_TASKS.includes(pendingTask)) {
      if (activeTab !== 'overview') {
        setActiveTab('overview');
      }

      setActiveMultiView(summaryView);
      return;
    }

    if (CALL_RECORDS_INTENT_TASKS.includes(pendingTask)) {
      if (activeTab !== 'records') {
        setActiveTab('records');
      }

      return;
    }

    if (FOLLOW_UP_TASKS_INTENT_TASKS.includes(pendingTask)) {
      if (activeTab !== 'follow-up') {
        setActiveTab('follow-up');
      }
    }
  }, [enabled, pendingTask, activeTab, summaryView, setActiveTab, setActiveMultiView]);
}
